import GoApi from "@/libs/client/goApi/goApi";
import type { HotelBenefits } from "@/libs/client/goApi/history/historyServiceInterface/getHistoryHotelDetailInterface";
import type {
  DataHotelPrivileges,
  HotelPerkInterface,
  PartialCheckinCheckout,
  PerkDataInterface,
} from "@/libs/client/goApi/hotel/hotelServiceInterfaces/getHotelAvailableInterface";
import { filterEmptyValues } from "@/utils/common/mapHelper/mapHelperUtils";
import { mapPerkHistory } from "@/utils/data/mapPerkHistory";

import { getDataByLocalized } from "../useLocalizedTranslation";

interface SpecialPerkInterface {
  specialDealPerks?: Record<string, string[]>;
  perk?: PerkDataInterface;
}

// NOTE - hotel id for test 498271;
export const mapPerks = ({
  hotelId,
  privileges,
}: {
  hotelId: number;
  privileges?: DataHotelPrivileges | null;
}) => {
  const defaultData: PerkDataInterface = { freebies: [], privileges: [] };
  try {
    const checkHotelId = !hotelId && hotelId !== 0;
    if (checkHotelId) return defaultData;
    const checkPrivilege = !privileges?.perks.length;
    if (checkPrivilege) return defaultData;

    const filterPerks: HotelPerkInterface[] = privileges.perks.filter((perk) =>
      perk.ids.includes(hotelId),
    );
    const mapAllPerk = filterPerks.reduce((prev, currentData) => {
      return {
        freebies: [...prev.freebies, ...currentData.freebies],
        privileges: [...prev.privileges, ...currentData.privileges],
      };
    }, defaultData);
    return mapAllPerk;
  } catch (error) {
    return defaultData;
  }
};

export const getNumberFromString = (text?: string | null) => {
  const numbersOnly = text ? parseInt(text.match(/\d+/)?.[0] ?? "0", 10) : 0;
  return numbersOnly;
};

// NOTE - call privilege by hotel id
export const fetchPrivilege = async (params: {
  hotelId: number;
  checkin?: string;
  checkout?: string;
}): Promise<DataHotelPrivileges | null> => {
  try {
    if (!params?.hotelId) return null;
    const removeEmptyFelid = filterEmptyValues(params);
    const resp = await GoApi.hotel.getHotelPrivileges(
      removeEmptyFelid as PartialCheckinCheckout,
    );
    return resp.data;
  } catch (error) {
    return null;
  }
};

const defaultPerk = { freebies: [], privileges: [] };
const mapThirdPartyFreebies = ({
  specialDealPerks = { en: [], th: [] },
  perk = defaultPerk,
}: SpecialPerkInterface): PerkDataInterface => {
  try {
    const getPerkByLang = getDataByLocalized<string[]>(specialDealPerks) || [];
    const mapPerk = getPerkByLang.map((text) => ({
      name: text,
      detail: "",
    }));
    return {
      freebies: [...perk.freebies, ...mapPerk],
      privileges: perk.privileges,
    };
  } catch (error) {
    return defaultPerk;
  }
};

export const hotelHistoryMapPerk = (perk: HotelBenefits[] = []) => {
  return mapPerkHistory(perk);
};

export const usePrivilege = () => {
  return {
    fetchPrivilege,
    mapPerks,
    getNumberFromString,
    mapThirdPartyFreebies,
    hotelHistoryMapPerk,
  };
};
